// src/pages/vendor/VendorOrders.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  doc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase";
import { useOutletContext } from "react-router-dom";

const nf = new Intl.NumberFormat("fr-FR");

const VENDOR_STATUS = [
  { v: "pending", label: "En attente" },
  { v: "confirmed", label: "Confirmée" },
  { v: "preparing", label: "Préparation" },
  { v: "shipped", label: "Expédiée" },
  { v: "delivered", label: "Livrée" },
  { v: "cancelled", label: "Annulée" },
];

function money(x) {
  const n = Number(x || 0);
  return `${nf.format(n)} XOF`;
}

function fmtDate(ts) {
  try {
    const d = ts?.toDate ? ts.toDate() : ts ? new Date(ts) : null;
    if (!d) return "-";
    return d.toLocaleString("fr-FR");
  } catch {
    return "-";
  }
}

export default function VendorOrders({ vendorId: vendorIdProp, vendorName }) {
  // ✅ IMPORTANT : tous les hooks doivent être appelés avant tout return
  
  const outlet = useOutletContext?.() || {};
  const vendorId =
    (vendorIdProp && String(vendorIdProp)) ||
    (outlet?.vendorId && String(outlet.vendorId)) ||
    localStorage.getItem("vendorId") ||
    localStorage.getItem("currentVendorId") ||
    "";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [qText, setQText] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    if (!vendorId) return;
    setLoading(true);
    try {
      const base = collection(db, "orders");
      const q = query(base, orderBy("createdAt", "desc"), limit(80));
      const snap = await getDocs(q);
      const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      // ✅ ne garder que les commandes où ce vendeur existe dans vendors map
      const mine = docs.filter((o) => o?.vendors && o.vendors[vendorId]);
      setRows(mine);

      // si le selected n’existe plus, on le reset
      setSelected((prev) => {
        if (!prev) return prev;
        const found = mine.find((x) => x.id === prev.id);
        return found || null;
      });
    } finally {
      setLoading(false);
    }
  }, [vendorId]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const t = qText.trim().toLowerCase();
    return rows.filter((o) => {
      const v = o?.vendors?.[vendorId];
      if (!v) return false;

      const st = v.status || "pending";
      if (statusFilter && st !== statusFilter) return false;

      if (!t) return true;

      const name = (o?.customer?.name || "").toLowerCase();
      const phone = (o?.customer?.phone || "").toLowerCase();
      const id = (o?.id || "").toLowerCase();

      return name.includes(t) || phone.includes(t) || id.includes(t);
    });
  }, [rows, qText, statusFilter, vendorId]);

  async function updateVendorStatus(orderId, nextStatus) {
    if (!vendorId) return;
    setBusyId(orderId);
    try {
      await updateDoc(doc(db, "orders", orderId), {
        [`vendors.${vendorId}.status`]: nextStatus,
        updatedAt: serverTimestamp(),
      });

      setRows((prev) =>
        prev.map((o) => {
          if (o.id !== orderId) return o;
          return {
            ...o,
            vendors: {
              ...o.vendors,
              [vendorId]: { ...o.vendors[vendorId], status: nextStatus },
            },
          };
        })
      );

      setSelected((s) => {
        if (!s || s.id !== orderId) return s;
        return {
          ...s,
          vendors: {
            ...s.vendors,
            [vendorId]: { ...s.vendors[vendorId], status: nextStatus },
          },
        };
      });
    } finally {
      setBusyId(null);
    }
  }

  // ✅ maintenant on peut rendre un fallback propre
  if (!vendorId) {
    return (
      <div className="container py-3">
        <div className="alert alert-warning">
          <div className="fw-semibold">vendorId manquant</div>
          <div className="small">
            Stocke-le dans localStorage (<code>vendorId</code>) au login vendeur
            ou passe-le via <code>VendorLayout</code> / Outlet context.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-3">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div>
          <h3 className="mb-0">Mes commandes (Vendeur)</h3>
          <div className="small text-muted">
            {vendorName ? vendorName : vendorId}
          </div>
        </div>
        <button className="btn btn-outline-secondary" onClick={load} disabled={loading}>
          Rafraîchir
        </button>
      </div>

      <div className="card p-3 mb-3">
        <div className="row g-2 align-items-end">
          <div className="col-md-6">
            <label className="form-label">Recherche</label>
            <input
              className="form-control"
              value={qText}
              onChange={(e) => setQText(e.target.value)}
              placeholder="Client / téléphone / orderId"
            />
          </div>
          <div className="col-md-3">
            <label className="form-label">Statut vendeur</label>
            <select
              className="form-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">Tous</option>
              {VENDOR_STATUS.map((s) => (
                <option key={s.v} value={s.v}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-3 text-md-end">
            <div className="small text-muted">Résultats : {filtered.length}</div>
          </div>
        </div>
      </div>

      {loading && <div className="alert alert-info">Chargement…</div>}
      {!loading && filtered.length === 0 && (
        <div className="alert alert-light border">Aucune commande pour ce vendeur.</div>
      )}

      <div className="row g-3">
        <div className="col-lg-7">
          <div className="card">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Date</th>
                    <th>Client</th>
                    <th>Total vendeur</th>
                    <th>Statut</th>
                    <th style={{ width: 220 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((o) => {
                    const v = o?.vendors?.[vendorId];
                    return (
                      <tr key={o.id} onClick={() => setSelected(o)} style={{ cursor: "pointer" }}>
                        <td className="small">{fmtDate(o.createdAt)}</td>
                        <td>
                          <div className="fw-semibold">{o?.customer?.name || "-"}</div>
                          <div className="small text-muted">{o?.customer?.phone || "-"}</div>
                        </td>
                        <td className="fw-semibold">{money(v?.totalXof)}</td>
                        <td>
                          <span className="badge text-bg-secondary">{v?.status || "pending"}</span>
                        </td>
                        <td className="text-end">
                          <select
                            className="form-select form-select-sm"
                            value={v?.status || "pending"}
                            disabled={busyId === o.id}
                            onChange={(e) => updateVendorStatus(o.id, e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {VENDOR_STATUS.map((s) => (
                              <option key={s.v} value={s.v}>
                                {s.label}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="p-3 small text-muted">
              Note: pour un filtrage Firestore parfait, ajoute un champ{" "}
              <code>vendorIds: []</code> dans la commande (ex: ["v1","v2"]) pour pouvoir faire{" "}
              <code>where("vendorIds","array-contains", vendorId)</code>.
            </div>
          </div>
        </div>

        <div className="col-lg-5">
          <div className="card p-3">
            {!selected ? (
              <div className="text-muted">Clique une commande pour voir le détail.</div>
            ) : (
              <>
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <div className="fw-semibold">Commande #{selected.id}</div>
                    <div className="small text-muted">{fmtDate(selected.createdAt)}</div>
                  </div>
                  <span className="badge text-bg-dark">
                    {selected?.vendors?.[vendorId]?.status || "pending"}
                  </span>
                </div>

                <hr />

                <div className="mb-2 fw-semibold">Client</div>
                <div className="small">
                  <div>{selected?.customer?.name}</div>
                  <div>{selected?.customer?.phone}</div>
                  <div className="text-muted">
                    {selected?.customer?.address} — {selected?.customer?.city}
                  </div>
                </div>

                <hr />

                <div className="mb-2 fw-semibold">Articles (ce vendeur)</div>
                <div className="list-group">
                  {(selected?.vendors?.[vendorId]?.items || []).map((it, idx) => (
                    <div key={idx} className="list-group-item">
                      <div className="fw-semibold">{it.name}</div>
                      <div className="small text-muted">
                        {it.qty} × {money(it.priceXof)} ={" "}
                        <span className="fw-semibold">{money(it.lineTotalXof)}</span>
                      </div>
                    </div>
                  ))}
                </div>

                <hr />

                <div className="d-flex justify-content-between fw-semibold">
                  <span>Total vendeur</span>
                  <span>{money(selected?.vendors?.[vendorId]?.totalXof)}</span>
                </div>
                <div className="small text-muted">Qté: {selected?.vendors?.[vendorId]?.qty}</div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
